<template>
    <section class="col mt-2 mt-md-3 also-like-tabs">
        <SectionTitle :title="$t(sectionTitle)" />
        <ul class="nav nav-tabs custom-tabs d-flex justify-content-around mt-4" role="tablist">
            <li v-for="tab in visibleTabs" :key="tab" class="nav-item">
                <button
                    :class="['nav-link', { active: activeTab === tab }]"
                    @click="activeTab = tab"
                    type="button"
                >
                    {{ $t(`products.also_like.${tab}`) }}
                </button>
            </li>
        </ul>
        <div class="tab-content">
            <div
                v-for="tab in visibleTabs"
                :key="tab"
                class="tab-pane fade"
                :class="{ 'show active': activeTab === tab }"
            >
                <div class="row py-4">
                    <div
                        v-if="isLoading(tab)"
                        v-for="n in 4"
                        :key="n"
                        class="col-6 col-md-3 mb-4 pb-2 pb-sm-0 px-md-4"
                    >
                        <ProductThumbSmallBlur :effect="false" />
                    </div>
                    <div
                        v-else
                        v-for="item in items[tab]"
                        :key="item.id"
                        class="col-6 col-md-3 mb-4 pb-2 pb-sm-0 px-md-4"
                    >
                        <ProductThumbSmall v-if="small" :product="item" />
                        <ProductThumb v-else :product="item"/>
                    </div>
                </div>
                <div class="d-flex justify-content-center mt-2" v-if="items[tab].length">
                    <LoadingSpinner v-if="isLoadingMore(tab)" :is-loading="true" />
                    <button
                        v-else-if="showLoadMore(tab)"
                        class="see-more-button"
                        @click="loadMore(tab)"
                        type="button"
                    >
                        <span class="fw-bold text-uppercase">
                            {{ $t('products.load_more') }}
                          </span>

                        <span class="see-more-arrow">
                            <svg xmlns="http://www.w3.org/2000/svg"
                                 width="24"
                                 height="24"
                                 viewBox="0 0 16 16"
                                 fill="currentColor"
                            >
                              <path fill-rule="evenodd"
                                    d="M1.646 4.646a.5.5 0 0 1 .708 0L8
                                       10.293l5.646-5.647a.5.5 0 1
                                       1 .708.708l-6 6a.5.5 0 0
                                       1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
                              />
                            </svg>
                        </span>
                    </button>
                </div>
                <div v-else class="text-center">
                    {{ $t(`products.also_like_${tab}_empty`) }}
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
const SectionTitle = defineAsyncComponent(() => import('~/components/Product/SectionTitle.vue'))
const ProductThumbSmall = defineAsyncComponent(() => import('~/components/Product/Thumbs/ProductThumbSmall.vue'))
const ProductThumb = defineAsyncComponent(() => import('~/components/Product/Thumbs/ProductThumb.vue'))
const ProductThumbSmallBlur = defineAsyncComponent(() => import('~/components/Product/Thumbs/ProductThumbSmallBlur.vue'))
const LoadingSpinner = defineAsyncComponent(() => import('~/components/LoadingSpinner.vue'))
import { useProductPageStore } from '~/stores/ProductPageStore'

const props = defineProps({
    productIds: {
        type: Array,
        required: true,
        validator: value => value.every(id => typeof id === 'number')
    },
    sectionTitle: {
        type: String,
        default: 'products.also_like.title'
    },
    small: Boolean
})

const allTabs = ['series','weight','metal','year','new']
const activeTab = ref(allTabs[0])
const alsoLike = ref({})
const alsoLikeMeta = ref({})
const pages = ref({})
const loadingAlsoLike = ref({})
const loadingAll = ref(true)

const items = computed(() => {
    return allTabs.reduce((acc, t) => {
        acc[t] = alsoLike.value[t] || []
        return acc
    }, {})
})

const visibleTabs = computed(() =>
    loadingAll.value
        ? allTabs
        : allTabs.filter(t => items.value[t].length > 0)
)

watch(visibleTabs, tabs => {
    if (!loadingAll.value && !tabs.includes(activeTab.value)) {
        activeTab.value = tabs[0] || null
    }
})

const isLoading = type =>
    loadingAll.value || !!loadingAlsoLike.value[type]

const isLoadingMore = type =>
    !!loadingAlsoLike.value[type] && items.value[type].length > 0

const showLoadMore = type => {
    const meta = alsoLikeMeta.value[type]
    return items.value[type].length === 4 && meta && meta.current_page < meta.last_page
}

const fetchAlsoLikeAll = async () => {
    const types = ['series','weight','metal','year','new']
    types.forEach(t => { loadingAlsoLike.value[t] = true })

    try {
        const params = new URLSearchParams()
        props.productIds.forEach(id => params.append('product_ids[]', id))
        params.set('small', props.small);
        const response = await getFromClient(`products/also-like?${params.toString()}`, { loading: true })
        types.forEach(t => {
            const items = response.also_like[t] || []
            const meta  = response.pagination[t]
            alsoLike.value[t] = items
            alsoLikeMeta.value[t] = meta
            pages.value[t] = meta.current_page
            loadingAlsoLike.value[t] = false
        })
    } catch (error) {
        console.error('Error fetching also like products:', error)
        types.forEach(t => {
            loadingAlsoLike.value[t] = false
        })
    } finally {
        loadingAll.value = false
    }
}

const fetchAlsoLikeItem = async (type, loadMore = false) => {
    if (!pages.value[type]) {
        pages.value[type] = 1
    }

    try {
        const next = loadMore ? pages.value[type] + 1 : 1
        loadingAlsoLike.value[type] = true

        const params = new URLSearchParams()
        props.productIds.forEach(id => params.append('product_ids[]', id))
        params.append('type', type)
        params.append('page', next)
        params.append('small', props.small)
        const response = await getFromClient(
            `products/also-like?${params.toString()}`,
            { loading: true }
        )

        const items = response.also_like[type] || []
        const meta  = response.pagination[type]

        alsoLike.value[type] = loadMore
            ? [...(alsoLike.value[type] || []), ...items]
            : items
        alsoLikeMeta.value[type] = meta
        pages.value[type] = meta.current_page
    } catch (error) {
        console.error(`Error fetching also like products for type ${type}:`, error)
    } finally {
        loadingAlsoLike.value[type] = false
    }
}

const loadMore = type => fetchAlsoLikeItem(type, true)

onMounted(() => {
    fetchAlsoLikeAll()
})
</script>

<style scoped>
.tab-content { position: relative; min-height: 200px; }
.tab-pane { position: absolute; top: 0; left: 0; width: 100%; opacity: 0; visibility: hidden; transition: opacity 0.4s ease; }
.tab-pane.show.active { position: relative; opacity: 1; visibility: visible; }

.see-more-button {
    background: transparent;
    border: none;
    display: flex;
    flex-direction: column;
}
</style>
